import React from 'react';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import { mergePastedEmails, parsePastedEmails } from './additionalEmailUtils';

interface AdditionalEmailsEditorProps {
  /** Current list of additional addresses (may contain blank rows the user has not filled in yet). */
  value: string[];
  onChange: (next: string[]) => void;
  /** Validation message for the whole list, surfaced under the rows. */
  error?: string;
  /** Called whenever the list changes so the caller can clear a stale error. */
  onClearError?: () => void;
  disabled?: boolean;
  /** Helper text under the heading — describes what these addresses are used for. */
  description?: string;
}

/**
 * Editor for an entity's strictly-additional email addresses (Farmers, Labs).
 *
 * The primary address is never mirrored here — that rule is enforced server-side by
 * `AdditionalEmailValidation` and mirrored client-side by `validateAdditionalEmails`
 * (see ./additionalEmailUtils).
 *
 * Pasting a block of several addresses into any row fans them out into one row each — a single-line
 * input would otherwise silently concatenate them into one unusable string.
 */
export const AdditionalEmailsEditor: React.FC<AdditionalEmailsEditorProps> = ({
  value,
  onChange,
  error,
  onClearError,
  disabled = false,
  description = 'Optional extra recipients offered when sending documents.',
}) => {
  const handleChange = (index: number, email: string) => {
    const next = [...value];
    next[index] = email;
    onChange(next);
    onClearError?.();
  };

  const handleAdd = () => {
    onChange([...value, '']);
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
    onClearError?.();
  };

  /**
   * Fans a multi-address paste out across rows. A paste yielding one address (or none) falls through
   * to the browser so normal cursor/selection semantics are preserved for ordinary typing-and-pasting.
   */
  const handlePaste = (index: number, event: React.ClipboardEvent<HTMLDivElement>) => {
    const pasted = parsePastedEmails(event.clipboardData.getData('text'));
    if (pasted.length < 2) return;

    event.preventDefault();
    onChange(mergePastedEmails(value, index, pasted));
    onClearError?.();
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Additional Email Addresses
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {description}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        Tip: paste a list of addresses into any field to add them all at once.
      </Typography>
      <Stack spacing={1.5}>
        {value.map((email, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <TextField
              label={`Additional Email ${index + 1}`}
              type="email"
              fullWidth
              size="small"
              value={email}
              onChange={(e) => handleChange(index, e.target.value)}
              onPaste={(e) => handlePaste(index, e)}
              disabled={disabled}
              slotProps={{ input: { sx: { borderRadius: 2 } } }}
            />
            <IconButton
              aria-label={`Remove additional email ${index + 1}`}
              onClick={() => handleRemove(index)}
              disabled={disabled}
              sx={{ mt: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Stack>
      {error && (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
      <Button
        startIcon={<AddIcon />}
        onClick={handleAdd}
        disabled={disabled}
        size="small"
        sx={{ textTransform: 'none', mt: 1.5 }}
      >
        Add email
      </Button>
    </Box>
  );
};

export default AdditionalEmailsEditor;
